/**
 * Main App Component
 * Routing and layout - simplified for single user
 * Includes safeguards against infinite refresh loops
 */

import { lazy, Suspense, useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { ProtectedRoute } from './components/ProtectedRoute'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Toaster } from './components/ui/toaster'
import { LoadingSpinner } from './components/ui/loading-spinner'

// Lazy load heavy pages
const WhatsAppConfigPage = lazy(() => import('./pages/WhatsAppConfigPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const PositionHistoryPage = lazy(() => import('./pages/PositionHistoryPage'))
const IntelligencePage = lazy(() => import('./pages/IntelligencePage'))
const DiagnosticPage = lazy(() => import('./pages/DiagnosticPage'))

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-muted-foreground">Loading...</p>
      </div>
    </div>
  )
}

// Refresh loop detector with improved detection
function RefreshLoopDetector() {
  const location = useLocation()
  const navigationCountRef = useRef(0)
  const lastNavigationTimeRef = useRef(Date.now())
  const navigationHistoryRef = useRef<string[]>([])

  useEffect(() => {
    const now = Date.now()
    const timeSinceLastNav = now - lastNavigationTimeRef.current
    const currentPath = location.pathname

    // Reset counter if more than 5 seconds since last navigation (increased from 2)
    if (timeSinceLastNav > 5000) {
      navigationCountRef.current = 0
      navigationHistoryRef.current = []
    }

    // Track navigation history
    navigationHistoryRef.current.push(currentPath)
    if (navigationHistoryRef.current.length > 10) {
      navigationHistoryRef.current.shift()
    }

    navigationCountRef.current++
    lastNavigationTimeRef.current = now

    // Detect ping-pong pattern (A -> B -> A -> B)
    const history = navigationHistoryRef.current
    if (history.length >= 4) {
      const lastFour = history.slice(-4)
      if (lastFour[0] === lastFour[2] && lastFour[1] === lastFour[3] && lastFour[0] !== lastFour[1]) {
        console.error('[RefreshLoopDetector] Detected ping-pong navigation loop!')
        handleNavigationLoop()
        return
      }
    }

    // If more than 8 navigations in 5 seconds, we have a loop (increased thresholds)
    if (navigationCountRef.current > 8 && timeSinceLastNav < 5000) {
      console.error('[RefreshLoopDetector] Detected rapid navigation loop!')
      handleNavigationLoop()
      return
    }

    function handleNavigationLoop() {
      // Clear all storage to reset state
      try {
        localStorage.clear()
        sessionStorage.clear()
      } catch (e) {
        console.error('Failed to clear storage:', e)
      }

      // Show error message
      document.body.innerHTML = `
        <div style="display: flex; align-items: center; justify-center; min-height: 100vh; padding: 20px; font-family: system-ui;">
          <div style="text-align: center; max-width: 500px;">
            <h1 style="font-size: 24px; font-weight: bold; margin-bottom: 16px;">Navigation Loop Detected</h1>
            <p style="color: #666; margin-bottom: 24px;">
              The application detected an infinite navigation loop and has been stopped to prevent issues.
              Your session and cache have been cleared.
            </p>
            <button 
              onclick="window.location.href='/login'" 
              style="background: #3b82f6; color: white; padding: 12px 24px; border: none; border-radius: 6px; cursor: pointer; font-size: 16px;"
            >
              Go to Login
            </button>
          </div>
        </div>
      `
      
      // Stop all further navigation
      throw new Error('Navigation loop detected')
    }
  }, [location])

  return null
}

import { ReloadPrompt } from './components/ReloadPrompt'

import { GoogleOAuthProvider } from '@react-oauth/google'

function App() {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || 'dummy_client_id'
  
  return (
    <GoogleOAuthProvider clientId={clientId}>
      <ErrorBoundary>
        <BrowserRouter>
          <RefreshLoopDetector />
          <ReloadPrompt />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/diagnostic" element={<DiagnosticPage />} />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/whatsapp"
                element={
                  <ProtectedRoute>
                    <WhatsAppConfigPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/intelligence"
                element={
                  <ProtectedRoute>
                    <IntelligencePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/history"
                element={
                  <ProtectedRoute>
                    <PositionHistoryPage />
                  </ProtectedRoute>
                }
              />

              {/* Redirect root to login - ProtectedRoute will handle auth redirect */}
              <Route path="/" element={<Navigate to="/login" replace />} />

              {/* 404 - redirect to login */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>

          {/* Global toast notifications */}
          <Toaster />
        </BrowserRouter>
      </ErrorBoundary>
    </GoogleOAuthProvider>
  )
}

export default App
